import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Megaphone, Code, Cpu, Rocket, ShoppingCart, Lightbulb, ArrowRight } from 'lucide-react';
import ServiceDetailModal from './ServiceDetailModal';

// Import shared animations and components
import { AnimatedSection, AnimatedText, sectionVariants, itemVariants } from '../animations';

const ServicesSection = ({ allProjects }) => {
    const [selectedService, setSelectedService] = useState(null);

    const services = [
        { 
            icon: Code, 
            title: 'Web & App Engineering', 
            description: 'Building high-performance, resilient, and beautiful applications for any platform.',
            packages: [
                { name: 'Launchpad', price: 'LKR 35,000+', features: ['Responsive Website', 'CMS Integration', 'Basic SEO', '1 Month Support'] },
                { name: 'Pro Suite', price: 'LKR 75,000+', features: ['Custom App/Web', 'API Integration', 'Advanced Analytics', '6 Months Support'], highlight: true },
                { name: 'Enterprise', price: 'Custom', features: ['Full-Scale Platform', 'Dedicated Team', 'Ongoing Maintenance', 'SLA'] },
            ] 
        },
        { 
            icon: Cpu, 
            title: 'AI & Automation', 
            description: 'Leveraging intelligent systems to optimize workflows and unlock data-driven insights.',
            packages: [
                { name: 'Workflow Bot', price: 'LKR 50,000+', features: ['Process Analysis', 'Custom Automation', 'Integration', 'Training'] },
                { name: 'AI Model', price: 'LKR 500,000+', features: ['Data Analysis', 'Custom Model Dev', 'API Endpoint', 'Performance Tuning'], highlight: true },
                { name: 'AI Ecosystem', price: 'Custom', features: ['Full AI Strategy', 'Multiple Models', 'Data Pipeline', 'Dedicated Support'] },
            ]
        },
        { 
            icon: ShoppingCart, 
            title: 'E-commerce Platforms', 
            description: 'Designing frictionless, high-conversion online retail experiences.',
            packages: [
                { name: 'Storefront', price: 'LKR 50,000+', features: ['Shopify/WooCommerce', 'Payment Gateway', 'Product Setup', 'Training'] },
                { name: 'Custom Store', price: 'LKR 200,000+', features: ['Bespoke Design', 'Headless Commerce', 'Inventory Sync', 'SEO Optimized'], highlight: true },
                { name: 'Marketplace', price: 'Custom', features: ['Multi-vendor Platform', 'Commission System', 'Full Customization', 'Scalable Infra'] },
            ]
        },
        { 
            icon: Megaphone, 
            title: 'Digital Marketing', 
            description: 'Boosting your brand visibility and engagement through targeted online campaigns, SEO, social media, and content strategies.',
            packages: [
                { name: 'Basic', price: 'LKR 10,000+', features:['Social Media Setup', '2 Social Media Platforms', 'Basic SEO'] }, 
                { name: 'Pro', price: 'LKR 30,000+', features:['High Performence ADs', 'PPC Campaigns', 'Advanced SEO'] }, 
                { name: 'Full', price: 'Custom', features:['Comprehensive Strategy', 'All-Inclusive Services', 'Custom Reporting'] }
            ] 
        },
        { icon: Rocket, title: 'Startup Acceleration', description: 'Providing the tech backbone and strategic guidance to launch and scale new ventures.', packages: [
            { name: 'Basic', price: 'LKR 10,000+', features:['Business Analysis', 'MVP Planning', 'Tech Stack Advisory']}, 
            { name: 'Pro', price: 'LKR 40,000+', features:['Full MVP Development', 'Market Research', 'Launch Support']}, 
            { name: 'Full', price: 'Custom', features:['End-to-End Support', 'Custom Development', 'Post-Launch Optimization']}
        ] },
        { icon: Lightbulb, title: 'Smart Technologies', description: 'Integrating IoT, automation, and connected solutions for efficiency.', packages: [
            { name: 'Basic', price: 'LKR 2,000+', features:['Smart Home Devices', 'Basic Automation', 'Setup Assistance']}, 
            { name: 'Pro', price: 'LKR 800,000+', features:['Custom IoT Solutions', 'Data Analytics', 'Integration with Existing Systems'], highlight: true}, 
            { name: 'Full', price: 'Custom', features:['Comprehensive IoT Strategy', 'Full Smart System Integration', 'Ongoing Support']}
        ] },
    ];
    
    return (
        <>
            <AnimatedSection id="services" className="bg-slate-50 dark:bg-gray-900 text-slate-900 dark:text-white">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-16">
                        <AnimatedText text="Our Capabilities." className="text-4xl md:text-6xl font-bold" variants={sectionVariants}/>
                    </div>
                    <motion.div 
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                        variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
                    >
                        {services.map((service, index) => (
                            <motion.div
                                key={index}
                                className="bg-white dark:bg-gray-800 p-8 rounded-2xl border border-slate-200 dark:border-gray-700 hover:border-cyan-500 dark:hover:border-cyan-400 transition-shadow duration-300 group shadow-lg hover:shadow-xl cursor-pointer flex flex-col"
                                variants={itemVariants}
                                // --- THIS IS THE NEW PART ---
                                // On hover, the card will scale up, move up slightly, and get a more pronounced shadow.
                                whileHover={{ 
                                    scale: 1.05, 
                                    y: -10,
                                    boxShadow: "0px 20px 30px rgba(0, 0, 0, 0.2)"
                                }}
                                // This adds a nice springy feel to the animation.
                                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                onClick={() => setSelectedService(service)}
                            >
                                <div className="text-cyan-500 dark:text-cyan-400 mb-4">
                                    <service.icon size={40} />
                                </div>
                                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">{service.title}</h3>
                                <p className="text-slate-600 dark:text-gray-400 flex-grow">{service.description}</p>
                                <div className="flex items-center justify-end text-sm font-semibold text-cyan-500 dark:text-cyan-400 mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                                    View Details <ArrowRight size={16} className="ml-1" />
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </AnimatedSection>
            <ServiceDetailModal 
                service={selectedService} 
                projects={allProjects} 
                onClose={() => setSelectedService(null)} 
            />
        </>
    );
};

export default ServicesSection;
